import {
  Theme,
  ThemeOptions,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import { ReactNode } from 'react';
import OpenSans from '@/assets/OpenSans.ttf';
// Ensure Data Grid component keys are known to the theme typing.
// Premium's augmentation covers MuiDataGrid; the community package is a separate install.
import '@mui/x-data-grid-premium/themeAugmentation';

// Theme and component augmentations are declared in src/types/*.d.ts
// Local augmentations to ensure this file sees the extended palette and variants
declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      border: { primary: string };
      shadow: { light: string; medium: string; dark: string };
      overlay: {
        primary: string;
        primaryHover: string;
        selected: string;
        selectedHover: string;
      };
      icon: { inactive: string };
    };
  }
  interface PaletteOptions {
    custom?: {
      border?: { primary?: string };
      shadow?: { light?: string; medium?: string; dark?: string };
      overlay?: {
        primary?: string;
        primaryHover?: string;
        selected?: string;
        selectedHover?: string;
      };
      icon?: { inactive?: string };
    };
  }
}

/**
 * App-wide CSS rules (font face + shared `.wa-*` / flyout class styles).
 *
 * Every selector here is class-scoped or a `@font-face`, so it is safe to emit
 * in an embedded host page. The element-level baseline (`html`, `body`, `*`)
 * is NOT part of this string: `CssBaseline` adds it for the standalone app,
 * while embedded widgets scope it to their own container instead.
 */
export const APP_GLOBAL_STYLE_OVERRIDES = `
        @font-face {
          font-family: 'Open Sans';
          src: local('Open Sans'), local('Open Sans-Regular'), url(${OpenSans}) format('truetype');
           font-display: swap;
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        .MuiDataGrid-columnHeaders: {
              // Optional: Style headers
              border-bottom: none,
              border-top: none,
              border-left: none,
              border-right: none,
            },
        .wa-select-investors__content {
          display: flex;
          flex-direction: column;
          gap: 40px;
          align-items: flex-start;
        }
        .wa-search-input { width: 510px; max-width: 100%; margin-top: 24px; }
        .wa-select-investors__grid {
          height: 420px;
          width: 100%;
        }
        /* Legacy-style variable list (centered two-column layout) */
        .wa-invite-vars__container {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .wa-invite-vars {
          padding: 0px;
        }
        .wa-invite-vars__intro { margin: 0px 0px 10px 0px !important; }
        .wa-invite-vars__list { width: 100%; max-width: 720px; }
        .wa-invite-vars__code { font-family: monospace; margin: 0px !important; }
        .wa-invite-vars__desc { text-align: left; margin: 0px !important; line-height: 1.45; }
        .wa-invite-vars__note { display: block; margin: 10px 0px 0px 0px !important; }

        /* Invitation template rich text (mui-tiptap) — align with TextField outline radius */
        .wa-invite-richtext .MuiTiptap-FieldContainer-root {
          border-radius: 8px;
        }
        .wa-invite-richtext .MuiTiptap-RichTextField-menuBar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        .wa-invite-richtext .MuiTiptap-RichTextField-content {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }

        /* Shared modal text rhythm to match UploadAccountHistoryModal */
        .wa-modal-body1 {
          color: var(--mui-palette-text-secondary);
          margin-bottom: 16px;
          font-size: 14px;
          line-height: 1.5;
        }
        .wa-modal-body2 {
          color: var(--mui-palette-text-secondary);
          margin-bottom: 24px;
          font-size: 14px;
          line-height: 1.5;
        }
        .wa-dialog-title__h5 { font-size: 1.25rem; }
        .wa-dialog-actions--divider { border-top: 1px solid var(--divider, rgba(0,0,0,0.12)); justify-content: flex-end; }

        /* Hide h5 Typography in Account Details Flyout content */
        .account-details-flyout-content h5.MuiTypography-h5 {
          display: none;
        }

        /* Remove all styles from Paper components in Account Details Flyout content */
        .account-details-flyout-content .MuiPaper-elevation, .holding-details-flyout-content .MuiPaper-elevation {
          padding: 0 !important;
          margin: 0 !important;
          background: transparent !important;
          background-color: transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          border: none !important;
        }

        .account-details-flyout-content .MuiDataGrid-withBorderColor, .holding-details-flyout-content .MuiDataGrid-withBorderColor{
          border:none !important;
        }

        .MuiTablePaginationActions-root button{
          display: inline-block !important;
        }

        /* Flyout mobile overrides */
        @media (max-width:1201px) {
          .account-details-flyout .MuiDrawer-paper, .holding-details-flyout .MuiDrawer-paper {
            width:100% !important;
            position:relative !important;
          }
          .account-details-flyout-content, .holding-details-flyout-content {
            width:100% !important;
          }
        }

        /* (Removed Import Clients-specific classes in favor of component-level sx) */
      `;

const commonTheme: ThemeOptions = {
  breakpoints: {
    // Align global bands with app usage
    values: {
      xs: 0,
      sm: 800, // mobile ≤ 800px
      md: 1200, // tablet 801–1199px, desktop ≥ 1200px
      lg: 1536,
      xl: 1920,
      mobile: 800,
    },
  },
  typography: {
    fontFamily: "'Open Sans', sans-serif",
    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 400 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400 },
    caption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: 1.2,
    },
    overline: { fontWeight: 600 },
    button: {
      textTransform: 'none',
    },
  },
  palette: {
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0,0,0,0.6)',
    },
    primary: {
      main: '#2F4A7D',
      dark: '#3B5A8D',
    },
    success: {
      main: '#2E7D32',
    },
    error: {
      main: '#D32F2F',
    },
    custom: {
      border: {
        primary: '#E0E0E0',
      },
      shadow: {
        light: 'rgba(205, 205, 205, 0.12)',
        medium: 'rgba(205, 205, 205, 0.14)',
        dark: 'rgba(205, 205, 205, 0.20)',
      },
      overlay: {
        primary: 'rgba(25, 118, 210, 0.04)',
        primaryHover: 'rgba(47, 74, 125, 0.08)',
        selected: 'rgba(47, 74, 125, 0.08)',
        selectedHover: 'rgba(47, 74, 125, 0.12)',
      },
      icon: {
        inactive: 'rgba(0, 0, 0, 0.56)',
      },
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          [theme.breakpoints.down('xs')]: {
            '& .MuiFormControl-root, .MuiInputBase-root': {
              display: 'block',
              width: '100%',
            },
            '& .MuiDataGrid-filterForm': {
              display: 'block',
              width: '100%',
            },
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({
          ownerState,
          theme,
        }: {
          ownerState: import('@mui/material/Button').ButtonProps;
          theme: Theme;
        }) => ({
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 8,
          boxShadow: `0px 1px 5px 0px ${theme.palette.custom.shadow.light}, 0px 2px 2px 0px ${theme.palette.custom.shadow.medium}, 0px 3px 1px -2px ${theme.palette.custom.shadow.dark}`,
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontWeight: 600,

          textTransform: 'none',
          ...(ownerState.size === 'large'
            ? {
                // Large styles
                padding: '8px 22px',
                fontSize: '15px',
                lineHeight: '26px',
              }
            : ownerState.size === 'small'
              ? {
                  // Small styles
                  padding: '4px 10px',
                  fontSize: '13px',
                  lineHeight: '22px',
                }
              : {
                  // Medium (default) styles
                  padding: '8px 16px',
                  fontSize: '14px',
                  lineHeight: '24px',
                }),
          '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiSvgIcon-root': {
            width:
              ownerState.size === 'large'
                ? 24
                : ownerState.size === 'small'
                  ? 18
                  : 20,
            height:
              ownerState.size === 'large'
                ? 24
                : ownerState.size === 'small'
                  ? 18
                  : 20,
            flexShrink: 0,
          },

          '& .MuiButton-endIcon': {
            marginTop: 0,
          },
          // ...(ownerState.variant === 'outlined' && {
          //   borderColor: theme.palette.primary.main,
          //   color: theme.palette.primary.main,
          //   '&:hover': {
          //     borderColor: theme.palette.primary.main,
          //     backgroundColor: theme.palette.custom.overlay.primary,
          //   },
          // }),
          ...(ownerState.variant === 'text' && {
            boxShadow: 'none',
          }),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: 8,
          background: theme.palette.background.paper,
          borderRadius: '8px',
          border: `1px solid ${theme.palette.custom.border.primary}`,
          boxShadow: `0px 1px 5px 0px ${theme.palette.custom.shadow.light}, 0px 2px 2px 0px ${theme.palette.custom.shadow.medium}, 0px 3px 1px -2px ${theme.palette.custom.shadow.dark}`,

          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          display: 'flex',
          padding: '8px 16px',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '24px',
          letterSpacing: '0.4px',
          color: theme.palette.text.secondary,

          '& .MuiTypography-root': {
            color: theme.palette.text.primary,
          },

          '&.Mui-selected': {
            borderRadius: 0,
            background: theme.palette.custom.overlay.selected,
            color: theme.palette.primary.main,
            '&:hover': {
              background: theme.palette.custom.overlay.selectedHover,
            },
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px',
          // Ensure top padding on content when preceded by a title, with higher specificity
          '& + .MuiDialogContent-root, & + .MuiDialogContent-root.MuiDialogContent-root':
            {
              paddingTop: '40px !important',
            },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '40px 24px',
          // Reinforce top padding when adjacent to a title (duplicate class increases specificity)
          '.MuiDialogTitle-root + &, .MuiDialogTitle-root + &.MuiDialogContent-root':
            {
              paddingTop: '40px !important',
            },
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    // Custom classes used by SelectInvestorsModal
    // Merged into the single MuiCssBaseline block below to avoid duplicate keys
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ ownerState }: { ownerState: { fullScreen?: boolean } }) => ({
          ...(ownerState.fullScreen && {
            paddingTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            paddingBottom:
              'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
            paddingLeft:
              'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
            paddingRight:
              'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
          }),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          '& .MuiAlert-icon': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAlert-action': {
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: '8px',
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${theme.palette.error.main}`,
          },
          '& .MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
            padding: '0px',
          },
        }),
        notchedOutline: {
          zIndex: 0,
        },
        input: ({
          ownerState,
          theme,
        }: {
          ownerState: import('@mui/material/OutlinedInput').OutlinedInputProps;
          theme: Theme;
        }) => ({
          ...(!ownerState.multiline && {
            height: '24px',
            minHeight: '24px',
          }),
          padding: '16px 12px',
          lineHeight: '24px',
          fontFamily: "'Open Sans', sans-serif",
          fontSize: '16px',
          fontWeight: 400,
          letterSpacing: '0.15px',
          fontFeatureSettings: "'liga' off, 'clig' off",
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          // Safari-specific rendering quirk: the outlined border can paint over the shrunk label.
          // Ensuring the label is above the outline (and giving it a small background) prevents the issue.
          zIndex: 1,
          '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            backgroundColor: theme.palette.background.paper,
            padding: '0 4px',
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: '8px',
          color: theme.palette.text.primary,
        }),
        select: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: '8px 16px',
          marginRight: theme.spacing(1),
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '14px',
          color: theme.palette.text.secondary,
          backgroundColor: 'transparent',
          borderRadius: '8px',
          minHeight: '40px',
          height: '40px',
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '&.Mui-selected': {
            backgroundColor: theme.palette.custom.overlay.selected,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          gap: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          display: 'flex',
          padding: '8px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          flex: '1 0 0',
          borderRadius: '8px !important',
          border: 'none',
          background: 'transparent',
          boxShadow: 'none',
          '& .MuiSvgIcon-root': {
            width: 24,
            height: 24,
            color: theme.palette.custom.icon.inactive,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected': {
            background: theme.palette.custom.overlay.selected,
            '& .MuiSvgIcon-root': {
              color: theme.palette.primary.main,
            },
            '&:hover': {
              background: theme.palette.custom.overlay.selectedHover,
            },
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: () => ({
          // Override MuiIconButton to match Select
          '& .MuiAutocomplete-popupIndicator, .MuiAutocomplete-clearIndicator':
            {
              height: 32,
              width: 32,
              margin: 2,
              border: 'none',
              boxShadow: 'none',
            },
        }),
      },
    },
    // Date picker styling to match design specifications (targeting classnames since MUI API may differ)
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiPickersOutlinedInput-root': {
            display: 'flex',
            padding: '9px 16px',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: 8,
            border: `0.5px solid ${theme.palette.custom.border.primary}`,
            background: theme.palette.background.paper,
            boxShadow: `0px 1px 5px 0px ${theme.palette.custom.shadow.light}, 0px 2px 2px 0px ${theme.palette.custom.shadow.medium}, 0px 3px 1px -2px ${theme.palette.custom.shadow.dark}`,

            '& .MuiPickersInputBase-sectionsContainer': {
              color: theme.palette.text.primary,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "'Open Sans', sans-serif",
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              padding: '0px',
            },

            '& .MuiInputAdornment-root': {
              marginLeft: '12px',

              '& .MuiSvgIcon-root': {
                width: 24,
                height: 24,
                flexShrink: 0,
              },
            },
          },
          '& .MuiPickersOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '--DataGrid-t-color-background-base': 'transparent !important',
          '& .MuiIconButton-root .MuiSvgIcon-root': {
            width: 20,
            height: 20,
          },
          // Hide multi-sort order badge in column headers
          '& .MuiDataGrid-columnHeader .MuiBadge-badge': {
            display: 'none',
          },
          // Default grid typography sizes
          '& .MuiDataGrid-cellContent': {
            fontSize: '14px',
            fontWeight: 400,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '14px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          // Typography components within DataGrid cells
          '& .MuiDataGrid-cell .MuiTypography-root': {
            fontSize: '14px',
            fontWeight: 400,
          },
          // Standardized caption styling for grid cells
          '& .MuiDataGrid-cell .MuiTypography-caption': {
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.2,
            color: theme.palette.text.secondary,
          },
          // Specific styling for data grid search inputs to reduce padding
          '& .MuiInputBase-root': {
            '& .MuiInputBase-input.MuiInputBase-inputSizeSmall': {
              padding: '8px 12px',
              height: '20px',
              minHeight: '20px',
            },
            // Remove border from input buttons
            '& .MuiIconButton-root': {
              border: 'none',
              boxShadow: 'none',
            },
          },
          'MuiInputBase-root': {},
          // Styling for Select components within DataGrid cells (like SegmentSelectCell)
          '& .MuiDataGrid-cell .MuiSelect-select': {
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: "'Open Sans', sans-serif",
            lineHeight: '24px',
            letterSpacing: '0.15px',
          },
          // Ensure Select components in DataGrid cells have consistent styling
          '& .MuiDataGrid-cell .MuiSelect-root': {
            '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall':
              {
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: "'Open Sans', sans-serif",
                lineHeight: '24px',
                letterSpacing: '0.15px',
              },
          },

          // Style expanded rows to match hover color
          "& .MuiDataGrid-row[aria-expanded='true']": {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          // Make pagination arrow buttons display inline (side by side)
          '& .MuiDataGrid-pagination': {
            '& .MuiTablePagination-actions': {
              display: 'flex',
              flexDirection: 'row',
              '& .MuiIconButton-root': {
                display: 'inline-block',
              },
            },
          },
          // Common DataGrid styling applied globally
          border: 'none',
          height: '100%',
          '& .MuiDataGrid-root': {
            minWidth: 0,
          },
          '& .MuiDataGrid-main': {
            minWidth: 0,
          },
          '& .MuiDataGrid-virtualScroller': {
            borderRadius: '8px',
            overflow: 'overlay',
            minWidth: 0,
          },
          '& .MuiDataGrid-topContainer ': {
            boxShadow: '0px 0px 4px rgba(0,0,0,0.05)',
          },
          // TODO: remove, when grids have been updated.
          '& .MuiDataGrid-toolbar .MuiDivider-vertical': {
            display: 'none',
          },
          // Target individual header cells for border radius
          '& .MuiDataGrid-columnHeader:first-of-type': {
            borderTopLeftRadius: '8px',
          },
          '& .MuiDataGrid-columnHeader:last-of-type': {
            borderTopRightRadius: '8px',
          },
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
            gap: '12px',
          },
          '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
            borderBottom: '0',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: 'none',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            gap: '8px',
          },
          '& .MuiDataGrid-main .MuiButtonBase-root': {
            border: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-footerContainer .MuiButtonBase-root': {
            border: 'none',
            boxShadow: 'none',
          },
          '& .MuiDataGrid-columnHeader:hover .MuiButtonBase-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-columnHeader:active .MuiButtonBase-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-columnHeaderDraggableContainer > .MuiDataGrid-menuIcon':
            {
              marginLeft: '4px',
            },
          '& .MuiDataGrid-cell': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          // EUD-619: The detail-panel toggle cell's right padding left a transparent
          // gap that exposed a stray dot beside the "+" expander (the dot was never in
          // the cell's own markup — it bled through that gap). Zeroing the padding
          // removes it; the "+" still works and greys out on single-tax-lot rows.
          // Only the Gains & Losses grids render this cell, so this is scoped to them.
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"]': {
            paddingRight: 0,
          },
          '& .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          // Style grouping cells to better accommodate text
          '& .MuiDataGrid-row--group': {
            '& .MuiDataGrid-cell:first-of-type': {
              minWidth: '200px !important',
              '& .MuiDataGrid-groupingCriteriaCell': {
                width: '100%',
                '& .MuiDataGrid-groupingCriteriaCellContent': {
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            },
          },
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: APP_GLOBAL_STYLE_OVERRIDES,
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: () => ({
          '&.gradient-row': {
            background:
              'linear-gradient(90deg, rgba(214, 222, 226, 0.20) 0%, rgba(214, 222, 226, 0.50) 100%)',
          },
          '@media (max-width:800px)': {
            '&.gradient-row': {
              borderRadius: 16,
              overflow: 'hidden',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            },
          },
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: () => ({
          paddingTop:
            'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          paddingBottom:
            'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          paddingLeft:
            'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
          paddingRight:
            'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
          '& .MuiIconButton-root': {
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            padding: '8px 8px 12px 8px',
          },
        }),
      },
    },
  },
};

export const LightTheme: Theme = responsiveFontSizes(
  createTheme({
    ...commonTheme,
    palette: {
      ...commonTheme.palette,
      background: {
        default: '#F5F7F8',
      },
    },
  }),
);

export const DarkTheme: Theme = responsiveFontSizes(
  createTheme({
    ...commonTheme,
    palette: {
      ...commonTheme.palette,
      mode: 'dark',
      text: {
        primary: 'rgba(255, 255, 255, 0.87)',
        secondary: 'rgba(255, 255, 255, 0.6)',
      },
      primary: {
        main: '#A0B3DF',
        dark: '#8C9BCF',
      },
      background: {
        default: '#121212', // Dark theme default background
        paper: '#1E1E1E', // Dark theme paper background
      },
      custom: {
        border: {
          primary: '#424242', // Darker border for dark theme
        },
        shadow: {
          light: 'rgba(0, 0, 0, 0.12)',
          medium: 'rgba(0, 0, 0, 0.14)',
          dark: 'rgba(0, 0, 0, 0.20)',
        },
        overlay: {
          primary: 'rgba(160, 179, 223, 0.08)', // Based on dark theme primary
          primaryHover: 'rgba(160, 179, 223, 0.12)',
          selected: 'rgba(160, 179, 223, 0.08)',
          selectedHover: 'rgba(160, 179, 223, 0.12)',
        },
        icon: {
          inactive: 'rgba(255, 255, 255, 0.56)',
        },
      },
    },
    components: {
      ...commonTheme.components,
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'initial',
          }),
        },
      },
    },
  }),
);

export const mobileWidthValue = 920;
export const mobileWidth = '(max-width:920px)';
/** MUI X desktop (popper) mode — complements `mobileWidth` so narrow viewports use full-screen dialog even with a fine pointer (mouse). */
export const pickerDesktopModeMediaQuery =
  '@media (min-width: ' + String(mobileWidthValue + 1) + 'px)';

// Dashboard-specific breakpoint:
// We switch the dashboard to its "mobile/XS" layout earlier than the rest of the app
// because the sticky Balance Sheet rail + right column becomes too cramped.
export const dashboardMobileWidthValue = 920;
export const dashboardMobileWidth = '(max-width:920px)';

// Sidebar-specific breakpoint:
// Between this width and `mobileWidthValue`, we force the permanent sidebar into its
// collapsed ("skinny") mode to keep content usable.
export const sidebarCollapsedWidthValue = 1120;
export const sidebarCollapsedWidth = '(max-width:1120px)';

export enum ThemeModeEnum {
  light = 'light',
  dark = 'dark',
  system = 'system',
}

interface ThemeInterface {
  code: ThemeModeEnum;
  icon: ReactNode;
}

export const Themes: Array<ThemeInterface> = [
  {
    code: ThemeModeEnum.dark,
    icon: <DarkModeOutlinedIcon />,
  },
  {
    code: ThemeModeEnum.light,
    icon: <LightModeOutlinedIcon />,
  },
  {
    code: ThemeModeEnum.system,
    icon: <SettingsBrightnessOutlinedIcon />,
  },
];
